import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/Dialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNewWorkspaceModalValues, useCloseNewWorkspaceModal } from './state'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreateWorkspaceMutation } from '../../mutations'
import {
  useChangeDashboardMutation,
  useWorkspaceList,
} from '~/app/(dashboard)/dashboardQuery'
import { SearchableInputField } from '~/components/SearchableInputField'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { WorkspacListQuery } from '~/gql/graphql'
import { EditWorkspaceIcon } from '../icons'
import { toast } from 'react-hot-toast'
import { useTranslation } from '~/i18n/useTranslation'

type WorkspaceListingItem = Pick<
  WorkspacListQuery['getWorkspacesForMember'][0],
  'id' | 'commonName'
>

const formSchema = z.object({
  parent: z
    .object({
      id: z.string(),
      commonName: z.string(),
    })
    .nullable(),
  workspaceName: z
    .string()
    .min(3, 'Name must be at least 3 characters long.')
    .max(200, 'Name cannot be greater than 200 characters.'),
  projectCode: z
    .string()
    .max(200, 'Project code cannot be greater than 200 characters.')
    .optional(),
})

type FormSchema = z.infer<typeof formSchema>

const defaultValues = {
  parent: null,
  workspaceName: '',
  projectCode: '',
}

export function NewWorkspaceModal() {
  const { t } = useTranslation()
  const dashboardWorkspaceContext = useDashboardWorkspaceContext()
  const response = useWorkspaceList(dashboardWorkspaceContext.memberId || '')
  const memberWorkspaces = useMemo(
    () => response?.data?.getWorkspacesForMember ?? [],
    [response?.data?.getWorkspacesForMember]
  )
  // The dashboardWorkspaceContext.workspace attribute values seem different than
  // the workspace in getWorkspacesForMember which should be correct.
  // So fetching the active workspace from that list for now.
  const activeWorkspace = useMemo(
    () =>
      memberWorkspaces.find(
        (ws) => ws.id === dashboardWorkspaceContext.workspace.id
      ),
    [memberWorkspaces, dashboardWorkspaceContext.workspace.id]
  )

  const { isOpen, parentId, isSameOrgOnly } = useNewWorkspaceModalValues()

  const memberWorkspacesFiltered = useMemo(
    () =>
      memberWorkspaces?.filter((workspace) => {
        if (isSameOrgOnly && workspace.orgName !== activeWorkspace?.orgName) {
          return false
        }
        return true
      }) ?? [],
    [memberWorkspaces, activeWorkspace, isSameOrgOnly]
  )

  const close = useCloseNewWorkspaceModal()
  const [isLoading, setIsLoading] = useState(false)
  const createWorkspace = useCreateWorkspaceMutation()
  const changeDashboard = useChangeDashboardMutation()

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    setFocus,
    formState: { isDirty, errors },
  } = form

  useEffect(() => {
    const targetParent = memberWorkspaces.find((ws) => ws.id === parentId)
    form.reset({
      parent: targetParent
        ? {
            id: targetParent.id,
            commonName: targetParent.commonName,
          }
        : null,
    })
  }, [memberWorkspaces, parentId])

  const handleSave = useCallback(
    async (values: FormSchema) => {
      if (!isDirty) {
        close()
        return
      }
      setIsLoading(true)

      try {
        await createWorkspace.mutateAsync(
          {
            workspaceName: values.workspaceName,
            parentId: values.parent?.id ?? dashboardWorkspaceContext.memberId,
            setupData: {
              billingCode: values.projectCode,
            },
          },
          {
            onSuccess(data) {
              if (!data?.createWorkspace?.id) {
                return
              }

              changeDashboard.mutateAsync(data.createWorkspace.id, {
                onSuccess() {
                  close()
                  setIsLoading(false)
                },
              })
            },
            onError(e: Error) {
              setIsLoading(false)
              toast.error(e.message)
            },
          }
        )
      } catch (e) {}
    },
    [isDirty, parentId]
  )

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        close()
        form.reset(defaultValues)
      }
    },
    [close]
  )

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setFocus('workspaceName')
      }, 10)
    }
  }, [isOpen])

  const isDisabled = useMemo(
    () => !!(isLoading || !isDirty || errors.workspaceName),
    [isLoading, isDirty, errors.workspaceName]
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="px-5 py-5 w-[37.5rem]" placement="center">
        <DialogHeader className="flex gap-4 items-center">
          <div className="bg-[#F4F6F7] p-2 rounded-lg">
            <EditWorkspaceIcon className="w-6" />
          </div>
          <DialogTitle className="text-black text-lg font-medium pr-4">
            {t('workspace.createNew')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col h-full"
          onSubmit={form.handleSubmit(handleSave)}
        >
          <Controller
            name="parent"
            control={form.control}
            render={({ field }) => {
              const { ref, ...remaining } = field
              return (
                <div className="mt-4">
                  <label htmlFor="parent" className="text-[#707070] text-sm">
                    Parent workspace (optional)
                  </label>
                  <SearchableInputField<WorkspaceListingItem | null>
                    {...remaining}
                    inputFieldRef={ref}
                    id="parent"
                    inputContainerClassName="mt-2"
                    placeholder={t('label.none')}
                    options={memberWorkspacesFiltered}
                    keyGetter={(option) => option?.id ?? ''}
                    labelGetter={(option) => option?.commonName ?? ''}
                    nullable
                  />
                  {errors.parent ? (
                    <p className="text-sm text-red-500 mt-2">
                      {errors.parent.message}
                    </p>
                  ) : null}
                </div>
              )
            }}
          />
          <div className="mt-4">
            <label htmlFor="workspaceName" className="text-[#707070] text-sm">
              Workspace name (required)
            </label>
            <input
              id="workspaceName"
              type="text"
              className="w-full p-3.5 mt-2 border border-[#D6D9DC] rounded-md resize-none placeholder:text-[#A9A9A9] text-sm"
              placeholder="Enter workspace name"
              {...form.register('workspaceName')}
            />
            {errors.workspaceName ? (
              <p className="text-sm text-red-500 mt-2">
                {errors.workspaceName.message}
              </p>
            ) : null}
          </div>

          <div className="mt-4">
            <label htmlFor="projectCode" className="text-[#707070] text-sm">
              Project code (optional)
            </label>
            <input
              id="projectCode"
              type="text"
              className="w-full p-3.5 mt-2 border border-[#D6D9DC] rounded-md resize-none placeholder:text-[#A9A9A9] text-sm"
              placeholder="Enter project code"
              {...form.register('projectCode')}
            />
            {errors.projectCode ? (
              <p className="text-sm text-red-500 mt-2">
                {errors.projectCode.message}
              </p>
            ) : null}
          </div>

          <footer className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center mt-10 gap-2">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <DialogClose asChild>
                <button
                  type="button"
                  onClick={() => close()}
                  className="px-7 py-3 rounded-xl border border-[#707070] text-[#707070] hover:border-gray-400 hover:text-gray-400 transition-colors font-semibold text-sm cursor-pointer"
                >
                  {t('action.cancel')}
                </button>
              </DialogClose>
              <button
                className="px-7 py-[0.8rem] rounded-xl text-white bg-primary-500 hover:bg-blue-500 transition-colors font-semibold text-sm cursor-pointer disabled:bg-blue-300 disabled:cursor-not-allowed"
                type="submit"
                disabled={isDisabled}
              >
                {t('action.save')}
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  )
}
